import { useCallback, useEffect, useRef } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

type StreamChunkHandler = (rawData: string) => void;

export function useChatTransport() {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const streamRequest = useCallback(
    async (
      url: string,
      body: object,
      onChunk: StreamChunkHandler,
      onError?: (error: unknown) => void,
    ): Promise<void> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch stream: ${response.status} ${errorText}`);
        }

        if (response.body === null) return;

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .pipeThrough(new EventSourceParserStream())
          .getReader();

        while (true) {
          const { value, done } = await reader.read();
          if (done || value.data === '\n\n') break;
          if (value.data) {
            onChunk(value.data);
          }
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        Sentry.captureException(error);
        onError?.(error);
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const buildClientChatUrl = useCallback(
    (clientId: string) => `${API_URL}/clients/${clientId}/chat`,
    [],
  );

  const buildCrossClientChatUrl = useCallback(() => `${API_URL}/chat/general`, []);

  const buildDocumentEditUrl = useCallback(
    (entityId: string) => `${API_URL}/documents/${entityId}/multi-turn-edit`,
    [],
  );

  return {
    streamRequest,
    abort,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildDocumentEditUrl,
  };
}
